import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {useTheme} from "@emotion/react";
import {getPercentage, getVisibleHourAndMinute} from "../../../common";
import CustomTooltip from "../../_common/component/CustomTooltip";
import CircleLoading from "../../_common/component/CircleLoading";
import {useSelector} from "react-redux";
import {useMobileDetect} from "../../../utils/Hooks";
import {useNavigate} from "react-router-dom";
import {
    CARD_TYPE_ATTENDANCE_RATE,
    CARD_TYPE_ATTENDANCE_TIME,
    CARD_TYPE_MOBILE_PERSONAL,
    CARD_TYPE_PERSONAL,
} from "../ContainerCourseList";
import {getImageUrl} from "../../../context/ApplicationContextProvider";
import LogoutButton from "../../_common/component/LogoutButton";
import {RIGHT_ARROW_TEXT} from "../../_presentational/texts";

/*
    props
    type : card_type_personal(1) || card_type_attendance_rate(2) || card_type_attendance_time(3) || card_type_mobile_personal(4)
        - personal : 개인정보(이름, 회사명, 이메일) || attendance_rate : 수강률 || attendance_obj : 이번 달, 종합 수강시간 || mobile_personal : mobile화면에서 보여줄 개인정보
    info : {name, email, company_name}
    attendance_obj : {this_month, total, total_attendance_rate}
    is_loading : bool
 */
const InfoCard = (props) => {
    const navigate = useNavigate();
    const account_reducer = useSelector((state) => state.account_reducer);
    const {is_mobile} = useMobileDetect();

    // state
    const [total_attendance_rate, setTotalAttendanceRate] = useState();
    const [this_month_time, setThisMonthTime] = useState();
    const [total_time, setTotalTime] = useState();

    // DOM ref
    const tooltip_position_ref = useRef();
    const attendance_rate_ref = useRef();

    // css style
    const {TimeRow, PercentRow, TooltipPosition, TitleRow, InfoCard, EmailRow} = useComponentStyle(
        is_mobile,
        props.type
    );

    useEffect(() => {
        if (props.attendance_obj) {
            setTotalAttendanceRate(props.attendance_obj.total_attendance_rate);
            setThisMonthTime(getVisibleHourAndMinute(props.attendance_obj.this_month));
            setTotalTime(getVisibleHourAndMinute(props.attendance_obj.total));
        }
    }, [props.attendance_obj]);

    return (
        <InfoCard className={props.className} type={props.type}>
            {props.type === CARD_TYPE_MOBILE_PERSONAL && (
                <>
                    {props.is_loading ? (
                        <PercentRow is_loading={props.is_loading}>
                            <CircleLoading size={"sm"} />
                        </PercentRow>
                    ) : (
                        <>
                            <TitleRow className="mobile-title-row">
                                <span>
                                    {account_reducer.info.name || "회원"} | {account_reducer.company.name}
                                </span>
                                <LogoutButton />
                            </TitleRow>
                            <div onClick={() => navigate("/account/update-info")}>
                                <RIGHT_ARROW_TEXT>{account_reducer.info.account_name}</RIGHT_ARROW_TEXT>
                            </div>
                        </>
                    )}
                </>
            )}
            {props.type === CARD_TYPE_PERSONAL && (
                <>
                    {props.is_loading ? (
                        <PercentRow is_loading={props.is_loading}>
                            <CircleLoading size={"sm"} />
                        </PercentRow>
                    ) : (
                        <>
                            <TitleRow>
                                <span>
                                    {account_reducer.info.name || "회원"} | {account_reducer.company.name}
                                </span>
                            </TitleRow>
                            <EmailRow>
                                <span>{account_reducer.info.account_name}</span>
                            </EmailRow>
                        </>
                    )}
                </>
            )}
            {props.type === CARD_TYPE_ATTENDANCE_RATE && (
                <>
                    <TitleRow>
                        <span className={"attendance_rate_title"}>수강률</span>
                        <TooltipPosition
                            ref={tooltip_position_ref}
                            onClick={() => {
                                if (is_mobile) {
                                    if (attendance_rate_ref.current.classList.value.split(" ").includes("active")) {
                                        attendance_rate_ref.current.classList.remove("active");
                                    } else {
                                        attendance_rate_ref.current.classList.add("active");
                                    }
                                }
                            }}>
                            {!is_mobile && (
                                <CustomTooltip width={"250px"} content={"수강한 클래스의 평균 수강률이에요"} />
                            )}
                        </TooltipPosition>
                        {is_mobile && (
                            <span className={"mobile-attendance-rate-info"} ref={attendance_rate_ref}>
                                수강한 클래스의 평균 수강률이에요
                            </span>
                        )}
                    </TitleRow>
                    {props.is_loading ? (
                        <PercentRow is_loading={props.is_loading}>
                            <CircleLoading size={"sm"} />
                        </PercentRow>
                    ) : (
                        <PercentRow is_loading={props.is_loading}>
                            {(total_attendance_rate && getPercentage(total_attendance_rate, "total_attendance_rate")) ||
                                0}
                            <span> %</span>
                        </PercentRow>
                    )}
                </>
            )}
            {props.type === CARD_TYPE_ATTENDANCE_TIME && (
                <>
                    <TitleRow>수강시간</TitleRow>
                    {props.is_loading ? (
                        <PercentRow is_loading={props.is_loading}>
                            <CircleLoading size={"sm"} />
                        </PercentRow>
                    ) : (
                        <>
                            <TimeRow>
                                <span>이번달</span>
                                <div>
                                    {this_month_time && this_month_time.hour}
                                    <span>시간</span>
                                    {this_month_time && this_month_time.minute}
                                    <span>분</span>
                                </div>
                            </TimeRow>
                            <TimeRow>
                                <span>전체</span>
                                <div>
                                    {total_time && total_time.hour}
                                    <span>시간</span>
                                    {total_time && total_time.minute}
                                    <span>분</span>
                                </div>
                            </TimeRow>
                        </>
                    )}
                </>
            )}
        </InfoCard>
    );
};

export default InfoCard;

export const useComponentStyle = (is_mobile, type) => {
    const theme = useTheme();
    return {
        InfoCard: styled.div({
            width: "100%",
            minWidth: "15em",
            height: "auto",
            boxSizing: "border-box",
            backgroundColor:
                type === CARD_TYPE_MOBILE_PERSONAL
                    ? "inherit"
                    : type === CARD_TYPE_PERSONAL
                    ? theme.colors.bearu_primary
                    : theme.colors.lg1,
            color:
                type === CARD_TYPE_MOBILE_PERSONAL
                    ? "inherit"
                    : type === CARD_TYPE_PERSONAL
                    ? theme.colors.w
                    : theme.colors.black,
            borderRadius: ".5em",
            padding: type === CARD_TYPE_PERSONAL ? "1em" : ".5em 1em",
            display: "flex",
            flexDirection: "column",
        }),
        TitleRow: styled.div({
            fontSize: theme.font_size.f18,
            width: "100%",
            height: "1.8em",
            fontWeight: type === CARD_TYPE_PERSONAL || type === CARD_TYPE_MOBILE_PERSONAL ? "500" : "800",
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            marginBottom: is_mobile ? "1.2em" : ".8em",
            justifyContent: type === CARD_TYPE_MOBILE_PERSONAL && "space-between",
            "& > .attendance_rate_title": {
                minWidth: "3em",
            },
            "& > span": {
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
            },
            "&.mobile-title-row": {
                marginBottom: ".5em",
            },
            "& > .mobile-attendance-rate-info": {
                fontSize: theme.font_size.f14,
                marginLeft: ".3em",
                color: theme.colors.dg1,
                fontWeight: "400",
                opacity: 0,
                transition: "all .2s",
                "@media (max-width: 320px)": {
                    fontSize: theme.font_size.f12,
                },
            },
            "& > .active": {
                opacity: 1,
            },
        }),
        TooltipPosition: styled.div({
            position: "relative",
            width: "1.2em",
            minWidth: "1.2em",
            height: "1.2em",
            cursor: "pointer",
            transform: "translateY(-3%)",
            background: `url(${getImageUrl("image/common/ic_tip_x3.png")}) center center/contain no-repeat`,
            "& > div": {
                div: {
                    opacity: "0",
                    transition: "opacity .2s",
                },
            },
            "&:hover": {
                div: {
                    opacity: "1",
                },
            },
        }),
        EmailRow: styled.div({
            fontSize: theme.font_size.f16,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
        }),
        PercentRow: styled.div((props) => ({
            fontSize: theme.font_size.f23,
            textAlign: props.is_loading ? "center" : "end",
            span: {
                fontSize: theme.font_size.f13,
            },
        })),
        TimeRow: styled.div((props) => ({
            marginBottom: ".3em",
            display: "flex",
            justifyContent: props.is_loading ? "center" : "space-between",
            "& > span": {
                display: "flex",
                alignItems: "end",
                fontSize: theme.font_size.f16,
            },
            div: {
                fontSize: theme.font_size.f23,
                span: {
                    fontSize: "18px",
                    margin: "0 .2em",
                },
            },
        })),
    };
};
